import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { setTop10Percentage } from "@pages/Statistics/store";

import { StatisticsContext } from "@pages/Statistics/context";

import { useResource } from "@hooks/useResource";

import {
    getAdvancedPeriodStringParam,
    getRoundedArrayNumbers,
    getSalesSupportQueryString,
    getStringsArrayUniqueValues,
    getTypeParam,
    capitalize,
    getUntranslatedString,
} from "@helpers/general";

import ErrorPage from "@components/Common/ErrorPage";
import CustomErrorBoundary from "@components/Common/CustomErrorBoundary";
import DonutChart from "@pages/Statistics/CommonCharts/DonutChart";
import LegendPopoverWrapper from "@pages/Statistics/CommonCharts/DonutChart/LegendPopoverWrapper";

export default function SalesSupportCol() {
    const { t } = useTranslation();

    const { period, type, advancedDate, selections } = useSelector(
        (state: any) => state
    );

    const { setPercentageData, salesSupportSelection } = React.useContext(
        StatisticsContext
    );

    const [data, setData] = React.useState<number[]>([]);
    const [labels, setLabels] = React.useState<string[]>([]);

    const { resourceData: salesSupportData, error } = useResource(
        `${
            process.env.REACT_APP_API_V1_URL
        }/statistics/products/percentage-sales-support?type=${getTypeParam(
            type
        )}&${getAdvancedPeriodStringParam(period, advancedDate)}${
            selections.designation && selections.designation !== "my_favorites"
                ? `&selected=${selections.designation}`
                : ""
        }${
            getSalesSupportQueryString(
                salesSupportSelection!.map(getUntranslatedString)
            ) !== ""
                ? `&sales-support=${getSalesSupportQueryString(
                      salesSupportSelection!.map(getUntranslatedString)
                  ).toUpperCase()}`
                : ""
        }`
    );

    React.useEffect(() => {
        let localSeries: number[] = [];
        let localSalesSupport: string[] = [];

        salesSupportData.data?.forEach((item: any) => {
            localSeries.push(item.value);
            localSalesSupport.push(item.designation);
        });

        setData(
            getRoundedArrayNumbers(localSeries.filter((item) => item !== 0))
        );

        setLabels(
            getStringsArrayUniqueValues(
                localSalesSupport.length > 0
                    ? localSalesSupport.map((item) => t(capitalize(item)))
                    : [t("No data")]
            )
        );

        setTop10Percentage(localSeries, "salesSupport");
    }, [salesSupportData, setPercentageData, t]);

    const donutChartSalesSupportData = {
        data,
        labels:
            labels.length > 0
                ? labels.map((label) => t(label))
                : [t("No data")],
    };

    return (
        <React.Fragment>
            {error ? (
                <ErrorPage />
            ) : (
                <CustomErrorBoundary>
                    <DonutChart
                        title={t("Sales support")}
                        data={donutChartSalesSupportData}
                    >
                        <LegendPopoverWrapper
                            title={t("Sales support")}
                            labels={labels || []}
                        />
                    </DonutChart>
                </CustomErrorBoundary>
            )}
        </React.Fragment>
    );
}
